// @ts-nocheck

import type {Dispatch, ThunkAction} from 'src/reducers';
import type {
  SankeyGraph,
  SankeyGraphVisits,
  SankeyGraphRequest,
  PeopleEngagedRequest,
  PeopleEnagedType,
  PresetQuestion,
  WhatsappDetails,
  Graph,
} from 'src/types/chatbot';

import {thunkify as flow} from 'src/utils/thunks';
import {key, fetching} from 'src/utils/redux';
import * as reduxApi from 'src/utils/redux-api-v2';
import {QUESTION_SIMILARITY_THRESHOLD} from 'src/components/conversation-builder/utils';


const {stringify} = JSON;

export const SANKEY_DATA = 'chatbot/sankey-graph';
export const SANKEY_VISITS_DATA = 'chatbot/sankey-graph-visits';
export const PEOPLE_ENGAGED_DATA = 'chatbot/people_engaged';
export const PEOPLE_ENGAGED_LOADING_DATA = 'chatbot/people_engaged_loading';
export const SANKEY_GRAPH_LOADING_DATA = 'chatbot/sankey_graph_loadingp';
export const RECEIVE_QUESTION_SIMILARITY =
  'chatbot/receive_question_similarity';
export const RECEIVE_PRESET_QUESTIONS = 'chatbot/receive_preset_questions';
export const RECEIVE_T2A_KEYWORDS = 'chatbot/receive_t2a_keywords';
export const RECEIVE_EXTERNAL_VARS = 'external-vars/receiveAll';
export const SET_WHATSAPP_DETAILS = 'chatbot/setWhatsappDetails';
export const SET_FLOW_DETAILS = 'chatbot/setFlowDetails';
export const RECEIVE_DISCOVER_JOB_ATTRIBUTES =
  'discover-job-attributes/receiveAll';
export const RECEIVE_TEMPLATE_PHONE_NUMBERS =
  'chatbot/receiveTemplatePhoneNumbers';

export type ExternalChatbotVariable = {
  data_type: string;
  entity_type: string;
  parameter: string;
};

export type DiscoverJobAttribute = {
  label: string;
  name: string;
  data_type: string;
};

let sankeyGraphApiDone = false;
let sankeyGraphVisitsApiDone = false;
type GetSankeyGraphDataAction = {
  type: typeof SANKEY_DATA;
  payload: Array<SankeyGraph> | [];
};

type GetSankeyGraphVisitsDataAction = {
  type: typeof SANKEY_VISITS_DATA;
  payload: Array<SankeyGraphVisits> | [];
};

type IsPeopleEngagedLoadingAction = {
  type: typeof PEOPLE_ENGAGED_LOADING_DATA;
  payload: boolean;
};

type IsSankeyGraphLoadingAction = {
  type: typeof SANKEY_GRAPH_LOADING_DATA;
  payload: boolean;
};

type GetPeopleEngagedAction = {
  type: typeof PEOPLE_ENGAGED_DATA;
  payload: Array<PeopleEngagedRequest>;
};

type ReceiveQuestionSimilarityAction = {
  type: typeof RECEIVE_QUESTION_SIMILARITY;
  payload: {
    [key: string]: string[];
  };
};

type ReceivePresetQuestionsAction = {
  type: typeof RECEIVE_PRESET_QUESTIONS;
  payload: PresetQuestion[];
};

type ReceiveT2AKeywordsAction = {
  type: typeof RECEIVE_QUESTION_SIMILARITY;
  payload: {
    [key: string]: number[];
  };
};

type ExternalChatbotVariablesAction = {
  type: typeof RECEIVE_EXTERNAL_VARS;
  payload: ExternalChatbotVariable[];
};

type SetWhatsappDetailsAction = {
  type: typeof SET_WHATSAPP_DETAILS;
  payload: WhatsappDetails;
};

type SetFlowDetailsAction = {
  type: typeof SET_FLOW_DETAILS;
  payload: Graph;
};

type DiscoverJobAttributesAction = {
  type: typeof RECEIVE_DISCOVER_JOB_ATTRIBUTES;
  payload: DiscoverJobAttribute[];
};

type ReceiveTemplatePhoneNumbersAction = {
  type: typeof RECEIVE_TEMPLATE_PHONE_NUMBERS;
  payload: Array<string>;
};

export type ChatbotActions =
  | GetSankeyGraphDataAction
  | GetSankeyGraphVisitsDataAction
  | GetPeopleEngagedAction
  | ReceiveQuestionSimilarityAction
  | ReceivePresetQuestionsAction
  | ReceiveT2AKeywordsAction
  | ExternalChatbotVariablesAction
  | SetWhatsappDetailsAction
  | SetFlowDetailsAction
  | DiscoverJobAttributesAction
  | ReceiveTemplatePhoneNumbersAction;

const getPeopleEngagedData = (
  payload: PeopleEngagedRequest[],
): GetPeopleEngagedAction => ({
  type: PEOPLE_ENGAGED_DATA,
  payload,
});

const getSankeyGraphData = (
  payload: Array<SankeyGraph> | [],
): GetSankeyGraphDataAction => ({
  type: SANKEY_DATA,
  payload,
});

const getSankeyGraphVisitsData = (
  payload: SankeyGraphVisits[] | [],
): GetSankeyGraphVisitsDataAction => ({
  type: SANKEY_VISITS_DATA,
  payload,
});

const isPeopleEngagedLoading = (
  payload: boolean,
): IsPeopleEngagedLoadingAction => ({
  type: PEOPLE_ENGAGED_LOADING_DATA,
  payload,
});

const isSankeyGraphLoading = (
  payload: boolean,
): IsSankeyGraphLoadingAction => ({
  type: SANKEY_GRAPH_LOADING_DATA,
  payload,
});

const receiveQuestionSimilarity = (payload: {
  [key: string]: string[];
}): ReceiveQuestionSimilarityAction => ({
  type: RECEIVE_QUESTION_SIMILARITY,
  payload,
});

const receivePresetQuestions = (payload: {
  [key: string]: string[];
}): ReceiveQuestionSimilarityAction => ({
  type: RECEIVE_PRESET_QUESTIONS,
  payload,
});

const receiveExternalChatbotVariables = (
  payload: unknown,
): ExternalChatbotVariablesAction => ({
  type: RECEIVE_EXTERNAL_VARS,
  payload,
});

const receiveDiscoverJobAttributes = (
  payload: unknown,
): DiscoverJobAttributesAction => ({
  type: RECEIVE_DISCOVER_JOB_ATTRIBUTES,
  payload,
});

const receiveTemplatePhoneNumbers = (
  payload: Array<string>,
): ReceiveTemplatePhoneNumbersAction => ({
  type: RECEIVE_TEMPLATE_PHONE_NUMBERS,
  payload,
});

export const getQuestionSimilarity =
  (questions: unknown): ThunkAction =>
  async (dispatch: Dispatch, getState: GetState) => {
    const existingQuestions = getState().chatbot.questionSimilarity;
    const filteredQuestions =
      questions?.filter((question: unknown) => !existingQuestions[question]) ??
      []; // is this weird to make the full question the key?

    if (filteredQuestions.length > 0) {
      dispatch(
        reduxApi.post('chatbot/questions/batch/similarity', {
          questions: filteredQuestions,
        }),
      ).then(
        (res) => {
          const normalizedResults = res.result.reduce<Record<string, any>>(
            (result, question) => {
              result[question.question] = question.predicted_labels.filter(
                ({score}) => score > QUESTION_SIMILARITY_THRESHOLD,
              );
              return result;
            },
            {},
          );
          dispatch(receiveQuestionSimilarity(normalizedResults));
        },
        (err) => {
          console.warn('unable to fetch question similarity');
        },
      );
    }
  };

export const getPresetQuestions =
  (): ThunkAction => async (dispatch: Dispatch) => {
    dispatch(reduxApi.get('chatbot/questions')).then((res) => {
      if (res?.questions) {
        dispatch(receivePresetQuestions(res.questions));
      } else {
        console.warn('error fetching preset questions');
      }
    });
  };

export const getExternalChatbotVariables =
  (): ThunkAction => async (dispatch: Dispatch) => {
    dispatch(reduxApi.get('chatbot/external-vars')).then((res) => {
      if (res) {
        dispatch(receiveExternalChatbotVariables(res));
      } else {
        console.warn('error fetching external variables');
      }
    });
  };

export const getDiscoverJobAttributes =
  (): ThunkAction => async (dispatch: Dispatch) => {
    dispatch(reduxApi.get('chatbot/job-response-metadata')).then((res) => {
      if (res) {
        dispatch(receiveDiscoverJobAttributes(res.discover_job_attributes));
      } else {
        console.warn('error fetching discover job attributes');
      }
    });
  };

// analytics/chatbot/sankey
export const updateChatBotSankeyGraph =
  (): ThunkAction => async (dispatch: Dispatch) => {
    dispatch(getSankeyGraphData([]));
    dispatch(getSankeyGraphVisitsData([]));
  };

export const isPeopleEngagedLoadingData =
  (isLoading: boolean): ThunkAction =>
  async (dispatch: Dispatch) => {
    dispatch(isPeopleEngagedLoading(isLoading));
  };

export const isSankeyGraphLoadingActionData =
  (isLoading: boolean): ThunkAction =>
  async (dispatch: Dispatch) => {
    dispatch(isSankeyGraphLoading(isLoading));
    dispatch(getSankeyGraphData([]));
    dispatch(getSankeyGraphVisitsData([]));
  };

export const fetchChatBotSankeyGraph: (
  chatbot_id: number,
) => ThunkAction<void> = flow(
  key((payload) => `analytics/chatbot/sankey_graph/${stringify(payload)}`),
  fetching(),
)(
  (chatbot_id: number) => (dispatch: Dispatch) =>
    dispatch(
      reduxApi.get(`analytics/chatbot/sankey_graph?chatbot_id=${chatbot_id}`),
    )
      .then((response) => {
        sankeyGraphApiDone = true;
        if (sankeyGraphVisitsApiDone) {
          dispatch(isSankeyGraphLoading(false));
        }
        dispatch(getSankeyGraphData(response));
      })
      .catch((err) => {
        dispatch(isSankeyGraphLoading(false));
      }),
);

export const fetchChatBotSankeyGraphVisits: (
  payload: SankeyGraphRequest,
) => ThunkAction<void> = flow(
  key((payload) => `analytics/chatbot/sankey_visits/${stringify(payload)}`),
  fetching(),
)(
  (payload: SankeyGraphRequest) => (dispatch: Dispatch) =>
    dispatch(reduxApi.post(`analytics/chatbot/sankey_visits`, payload))
      .then((response) => {
        sankeyGraphVisitsApiDone = true;
        if (sankeyGraphApiDone) {
          dispatch(isSankeyGraphLoading(false));
        }
        dispatch(getSankeyGraphVisitsData(response));
      })
      .catch((err) => {
        dispatch(isSankeyGraphLoading(false));
      }),
);

export const fetchChatBotPeopleEngaged: (
  payload: PeopleEngagedRequest,
) => ThunkAction<PeopleEnagedType[]> = flow(
  key((payload) => `analytics/chatbot/people_engaged/${stringify(payload)}`),
  fetching(),
)((payload: PeopleEngagedRequest) => (dispatch: Dispatch) => {
  dispatch(isPeopleEngagedLoading(true));
  return dispatch(
    reduxApi.post(`analytics/chatbot/people_engaged`, payload),
  ).then((response) => {
    dispatch(isPeopleEngagedLoading(false));
    dispatch(getPeopleEngagedData(response));
  });
});

export const getPreviewBeefreeHtml: (
  beefree_json: Record<any, any> | Record<any, any>,
) => ThunkAction<unknown> = (beefree_json) => (dispatch: Dispatch) => {
  return dispatch(reduxApi.post(`chatbot/beefree-html`, {beefree_json}));
};

export const setWhatsappDetails = (
  payload: WhatsappDetails,
): SetWhatsappDetailsAction => ({
  type: SET_WHATSAPP_DETAILS,
  payload,
});

export const setFlowDetails = (payload: Graph): SetFlowDetailsAction => ({
  type: SET_FLOW_DETAILS,
  payload,
});

export const getTemplatePhoneNumbers =
  (templateId: string): ThunkAction<Array<string>> =>
  async (dispatch: Dispatch) => {
    try {
      const response = await dispatch(
        reduxApi.get(`messages/fetch-phones/${templateId}`),
      );
      // Ensure response is an array
      const phoneNumbers = Array.isArray(response) ? response : [];
      dispatch(receiveTemplatePhoneNumbers(phoneNumbers));
      return phoneNumbers;
    } catch (error: any) {
      console.error('Error fetching template phone numbers:', error);
      dispatch(receiveTemplatePhoneNumbers([]));
      return [];
    }
  };
